
import { env } from "@/app/_utils/env";
import { getCsrfToken } from "@/app/_utils/csrf";

/**
 * Headers for a write.
 *
 * SessionAuthentication enforces CSRF on unsafe methods, so a POST/PUT/DELETE without
 * X-CSRFToken is rejected as 403 before the view runs. Read at call time rather than at
 * construction: the cookie is only set once the session exists, which is after this
 * module first loads.
 */
function writeHeaders() {
  return {
    "Content-Type": "application/json",
    "X-CSRFToken": getCsrfToken(),
  };
}

export type GuestOrderRow = {
  id: string;
  show_id: string | null;
  show_title: string | null;
  img_url: string | null;
  variant_name: string | null;
  quantity: number;
  price: number;
  redeemed: number;
  comped: boolean;
  payment_type: string | null;
  created_at: string;
};

export type GuestShowRow = {
  id: string;
  title: string;
  img_url: string | null;
  start: string;
  location_name: string | null;
  num_tickets: number;
  total_spend: number;
};

export type GuestKpis = {
  position: number | null;
  joined_date: number | null;
  signups_count: number;
  shows_attended: number;
  check_in_rate: number | null;
  lifetime_spend: number;
  orders_count: number;
};

class GuestService {
  static instance: GuestService;

  async getGuests({
    paginationModel,
    search,
    since,
    sortModel,
    filters,
  }: {
    paginationModel?: any;
    sortModel?: any;
    search?: string | undefined;
    since?: any;
    filters?: any;
  }): Promise<any> {
    const queryParams = new URLSearchParams();

    if (search) queryParams.append("search", search);
    if (since) queryParams.append("since", since);
    if (filters) queryParams.append("filters", JSON.stringify(filters));
    if (sortModel) queryParams.append("sort", JSON.stringify(sortModel));
    if (paginationModel) {
      let offest =
        paginationModel.page === 0
          ? 0
          : paginationModel.page * paginationModel.pageSize;
      queryParams.append("offset", offest.toString());
      queryParams.append("limit", paginationModel.pageSize.toString());
    }

    const res = await fetch(
      `${env.apiBaseUrl}guest/?${queryParams}`,
      { credentials: "include" },
    );

    if (!res.ok) {
      throw new Error(`Response status: ${res.status}`);
    }

    return res.json();
  }

  async createGuest(guest: any) {
    return fetch(`${env.apiBaseUrl}guest/`, {
      method: "POST",
      body: JSON.stringify(guest),
      headers: writeHeaders(),
      credentials: "include",
    });
  }

  async updateGuest(guest: any) {
    let newGuest = guest;
    const res = await fetch(`${env.apiBaseUrl}guest/`, {
      method: "PUT",
      body: JSON.stringify(newGuest),
      headers: writeHeaders(),
      credentials: "include",
    });
    return res.json();
  }

  async updateOrCreateGuest(guest: any) {
    let newGuest = guest;
    newGuest["waitlist"] = true;
    const res = await fetch(`${env.apiBaseUrl}guest/`, {
      method: "PUT",
      body: JSON.stringify(newGuest),
      headers: writeHeaders(),
      credentials: "include",
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        (body as { message?: string }).message || `Request failed: ${res.status}`,
      );
    }
    return body;
  }

  async deletGuest(id: string) {
    return fetch(`${env.apiBaseUrl}guest/?id=${id}`, {
      method: "DELETE",
      headers: writeHeaders(),
      credentials: "include",
    });
  }

  async getGuestLastShow(id: string) {
    const res = await fetch(
      `${env.apiBaseUrl}guest/show/?id=${id}&last=true`,
      { method: "GET", credentials: "include" },
    );
    if (!res.ok) throw new Error(`Response status: ${res.status}`);
    return res.json();
  }

  async getGuest(id: string) {
    const res = await fetch(`${env.apiBaseUrl}guest/?id=${id}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Response status: ${res.status}`);
    return res.json();
  }

  async getGuestKpis(id: string): Promise<GuestKpis> {
    const res = await fetch(`${env.apiBaseUrl}guest/kpis/?id=${id}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Response status: ${res.status}`);
    return res.json();
  }

  async getGuestOrders(id: string): Promise<GuestOrderRow[]> {
    const res = await fetch(`${env.apiBaseUrl}guest/orders/?id=${id}`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error(`Response status: ${res.status}`);
    return res.json();
  }

  async getGuestShows(id: string): Promise<GuestShowRow[]> {
    // hits guest_service.get_guest_show (not last)
    const res = await fetch(
      `${env.apiBaseUrl}guest/show/?id=${id}`,
      { method: "GET", credentials: "include" },
    );
    if (!res.ok) throw new Error(`Response status: ${res.status}`);
    return res.json();
  }

  async downloadWaitlistBulkTemplate(): Promise<Blob> {
    const res = await fetch(
      `${env.apiBaseUrl}guest/waitlist/template/`,
      { credentials: "include" },
    );
    if (!res.ok) {
      throw new Error(`Template download failed: ${res.status}`);
    }
    return res.blob();
  }

  async bulkWaitlistUpload(data: string): Promise<{
    created: number;
    updated: number;
    errors: {
      row: number;
      message: string;
      first_name: string;
      last_name: string;
    }[];
    results: {
      row: number;
      status: "created" | "updated" | "error";
      message: string | null;
      first_name: string;
      last_name: string;
    }[];
  }> {
    const res = await fetch(
      `${env.apiBaseUrl}guest/waitlist/bulk/`,
      {
        method: "POST",
        body: JSON.stringify({ data }),
        headers: writeHeaders(),
        credentials: "include",
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        (body as { message?: string }).message ||
          `Upload failed: ${res.status}`,
      );
    }
    return body as {
      created: number;
      updated: number;
      errors: {
        row: number;
        message: string;
        first_name: string;
        last_name: string;
      }[];
      results: {
        row: number;
        status: "created" | "updated" | "error";
        message: string | null;
        first_name: string;
        last_name: string;
      }[];
    };
  }
}

export default new GuestService();
